import {
  Box,
  InputAdornment,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import Fuse from 'fuse.js';
import {Search} from 'lucide-react';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';

import {useGetOrgUsersQuery} from '../../data/api';
import {DARK_TEXT_COLOR} from '../../theme';
import {Modal} from '../Modal';
import OverflowTooltip from './OverflowTooltip';

const styles = {
  container: {
    padding: '1rem 1.5rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
    height: '50vh',
    maxHeight: '50vh',
    minHeight: '300px',
  },
  searchInput: {
    marginBottom: '1rem',
    flexShrink: 0,
    '& .MuiOutlinedInput-root': {
      borderRadius: '0.5rem',
    },
    '& .MuiInputBase-input': {
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: DARK_TEXT_COLOR,
  },
};

const fuseOptions = {
  keys: ['firstName', 'lastName', 'email', 'mappedRole'],
  threshold: 0.3,
  ignoreLocation: true,
};

// Role mapping: If role is reviewer, mapped to Viewer, otherwise Editor
const mapRole = (role) => {
  if (!role) return '';
  return role.toLowerCase() === 'reviewer' ? 'Viewer' : 'Editor';
};

const getMappedRole = (roleInOrg, additionalRoles = []) => {
  const roles = [roleInOrg, ...additionalRoles].filter(Boolean);
  const mappedRoles = [...new Set(roles.map(mapRole))];
  return mappedRoles.length > 0 ? mappedRoles.join(', ') : '';
};

const OrgUsersModal = ({open, onClose, orgId, orgName}) => {
  const {data, isLoading} = useGetOrgUsersQuery({orgId}, {skip: !open});
  const {users = []} = data || {};
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState('lastName');
  const [order, setOrder] = useState('asc');

  // Add mapped role to users for searching and sorting
  const usersWithMappedRole = useMemo(() => {
    return users.map((user) => ({
      ...user,
      mappedRole: getMappedRole(user.roleInOrg, user.additionalRoles),
    }));
  }, [users]);

  const fuse = useMemo(
    () => new Fuse(usersWithMappedRole, fuseOptions),
    [usersWithMappedRole]
  );

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return usersWithMappedRole;
    }
    return fuse.search(searchQuery).map((result) => result.item);
  }, [searchQuery, usersWithMappedRole, fuse]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let valueA, valueB;

      if (orderBy === 'lastName') {
        valueA = (a.lastName || '').toLowerCase();
        valueB = (b.lastName || '').toLowerCase();
      } else if (orderBy === 'role') {
        valueA = (a.mappedRole || '').toLowerCase();
        valueB = (b.mappedRole || '').toLowerCase();
      }

      if (order === 'asc') {
        return valueA.localeCompare(valueB);
      }
      return valueB.localeCompare(valueA);
    });
  }, [filteredUsers, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const totalCount = users.length;
  const filteredCount = sortedUsers.length;
  const userCountDescriptor = searchQuery.trim()
    ? `${filteredCount} of ${totalCount}`
    : `${totalCount} total`;

  return (
    <Modal onClose={onClose} open={open} title={`${orgName} Users`}>
      <Box sx={styles.container}>
        {isLoading ? (
          <Stack direction="column" gap="0.5rem">
            {[...Array(3)].map((_, i) => (
              <Skeleton
                animation="wave"
                height={36}
                key={i}
                variant="rectangular"
              />
            ))}
          </Stack>
        ) : usersWithMappedRole.length === 0 ? (
          <Typography sx={styles.emptyState} variant="body2">
            No users found in this organization.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={styles.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />
            <TableContainer
              className="persist-scrollbar"
              sx={styles.tableContainer}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 700, fontSize: '0.75rem'}}>
                      <TableSortLabel
                        active={orderBy === 'lastName'}
                        direction={orderBy === 'lastName' ? order : 'asc'}
                        onClick={() => handleSort('lastName')}
                      >
                        <Stack
                          direction="row"
                          gap="0.125rem"
                          sx={{display: 'inline-flex', alignItems: 'baseline'}}
                        >
                          <Typography
                            sx={{fontWeight: 700, fontSize: '0.75rem'}}
                          >
                            Users
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '10px',
                              color: 'text.secondary',
                            }}
                          >
                            ({userCountDescriptor})
                          </Typography>
                        </Stack>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{fontWeight: 700, fontSize: '0.75rem'}}
                    >
                      <TableSortLabel
                        active={orderBy === 'role'}
                        direction={orderBy === 'role' ? order : 'asc'}
                        onClick={() => handleSort('role')}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography
                          sx={{...styles.emptyState, padding: '1rem'}}
                          variant="body2"
                        >
                          No users match your search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedUsers.map((user) => {
                      const displayName =
                        user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email;
                      return (
                        <TableRow key={user.email} hover>
                          <TableCell>
                            <Stack direction="column">
                              <OverflowTooltip
                                text={displayName}
                                passthroughProps={{
                                  sx: {fontWeight: 600},
                                }}
                              />
                              {displayName !== user.email && (
                                <OverflowTooltip
                                  text={user.email}
                                  passthroughProps={{
                                    color: 'text.secondary',
                                    variant: 'subtitle2',
                                    sx: {
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{fontSize: '0.75rem'}}
                            >
                              {user.mappedRole}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

OrgUsersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  orgName: PropTypes.string.isRequired,
};

export default OrgUsersModal;
